import React, { useState, useCallback } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { CheckCircleIcon as CheckCircle, DragIndicatorIcon as DragIndicator, ReplayIcon as Replay, SaveIcon as Save, VisibilityIcon as Visibility, VisibilityOffIcon as VisibilityOff } from "../../../../icons";
// ============================================================================
// Types
// ============================================================================

export interface ColumnMenuItem {
  id: string;
  header: string;
}

export interface ColumnMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  columns: ColumnMenuItem[];
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  columnOrder: string[];
  onColumnOrderChange: (order: string[]) => void;
  onResetColumnOrder?: () => void;
  onShowAllColumns?: () => void;
  onHideAllColumns?: () => void;
  onSaveFormat?: () => void;
  language?: 'es' | 'en' | 'pt';
}

// ============================================================================
// Localization
// ============================================================================

const localization: Record<string, { resetOrder: string; showAll: string; hideAll: string; save: string; saved: string }> = {
  es: {
    resetOrder: 'Restablecer',
    showAll: 'Mostrar todo',
    hideAll: 'Ocultar todo',
    save: 'Guardar',
    saved: 'Guardado',
  },
  en: {
    resetOrder: 'Reset',
    showAll: 'Show all',
    hideAll: 'Hide all',
    save: 'Save',
    saved: 'Saved',
  },
  pt: {
    resetOrder: 'Redefinir',
    showAll: 'Mostrar tudo',
    hideAll: 'Ocultar tudo',
    save: 'Salvar',
    saved: 'Salvo',
  },
};

// ============================================================================
// ColumnMenu Component
// ============================================================================

export const ColumnMenu: React.FC<ColumnMenuProps> = ({
  anchorEl,
  open,
  onClose,
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  onResetColumnOrder,
  onShowAllColumns,
  onHideAllColumns,
  onSaveFormat,
  language = 'es',
}) => {
  // Drag & Drop state
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);

  // Feedback visual del save: muestra CheckCircle verde por ~1.5s tras click
  const [justSaved, setJustSaved] = useState(false);
  const handleSaveClick = useCallback(() => {
    if (!onSaveFormat) return;
    onSaveFormat();
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 1500);
  }, [onSaveFormat]);

  const loc = localization[language] || localization.es;

  // Sort columns by current order
  const sortedColumns = React.useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return columns;

    const orderMap = new Map(columnOrder.map((id, idx) => [id, idx]));
    return [...columns].sort((a, b) => {
      const aIdx = orderMap.get(a.id) ?? Infinity;
      const bIdx = orderMap.get(b.id) ?? Infinity;
      return aIdx - bIdx;
    });
  }, [columns, columnOrder]);

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, columnId: string) => {
    setDraggedColumnId(columnId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedColumnId && columnId !== draggedColumnId) {
      setDragOverColumnId(columnId);
    }
  }, [draggedColumnId]);

  const handleDragLeave = useCallback(() => {
    setDragOverColumnId(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (!draggedColumnId || draggedColumnId === targetColumnId) {
      setDraggedColumnId(null);
      setDragOverColumnId(null);
      return;
    }

    // Calculate new order
    const currentOrder = columnOrder.length > 0 ? [...columnOrder] : columns.map(c => c.id);
    const draggedIndex = currentOrder.indexOf(draggedColumnId);
    const targetIndex = currentOrder.indexOf(targetColumnId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      currentOrder.splice(draggedIndex, 1);
      currentOrder.splice(targetIndex, 0, draggedColumnId);
      onColumnOrderChange(currentOrder);
    }

    setDraggedColumnId(null);
    setDragOverColumnId(null);
  }, [draggedColumnId, columnOrder, columns, onColumnOrderChange]);

  const handleDragEnd = useCallback(() => {
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  }, []);

  const handleVisibilityToggle = useCallback((columnId: string, checked: boolean) => {
    onColumnVisibilityChange({ ...columnVisibility, [columnId]: checked });
  }, [columnVisibility, onColumnVisibilityChange]);

  const hasActions = onShowAllColumns || onHideAllColumns || onResetColumnOrder || onSaveFormat;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { maxHeight: 400, minWidth: 200 } } }}
    >
      {/* Action buttons: Reset / Show All / Hide All / Save */}
      {hasActions && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 1, py: 0.5, gap: 0.5 }}>
          {onResetColumnOrder && (
            <Tooltip title={loc.resetOrder}>
              <IconButton size="small" onClick={onResetColumnOrder}>
                <Replay fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onShowAllColumns && (
            <Tooltip title={loc.showAll}>
              <IconButton size="small" onClick={onShowAllColumns}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onHideAllColumns && (
            <Tooltip title={loc.hideAll}>
              <IconButton size="small" onClick={onHideAllColumns}>
                <VisibilityOff fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onSaveFormat && (
            <Tooltip title={justSaved ? loc.saved : loc.save}>
              <IconButton size="small" onClick={handleSaveClick}>
                {justSaved
                  ? <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
                  : <Save fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      {hasActions && <Divider />}

      {/* Column list with switches (draggable) */}
      {sortedColumns.map(col => (
        <MenuItem
          key={col.id}
          draggable
          onDragStart={(e) => handleDragStart(e, col.id)}
          onDragOver={(e) => handleDragOver(e, col.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, col.id)}
          onDragEnd={handleDragEnd}
          sx={{
            py: 0.5,
            cursor: 'grab',
            opacity: draggedColumnId === col.id ? 0.5 : 1,
            backgroundColor: dragOverColumnId === col.id ? 'action.hover' : 'transparent',
            borderTop: dragOverColumnId === col.id ? '2px solid' : 'none',
            borderColor: 'primary.main',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicator fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={columnVisibility[col.id] !== false}
                onChange={(e) => handleVisibilityToggle(col.id, e.target.checked)}
              />
            }
            label={col.header}
            sx={{ width: '100%', m: 0 }}
          />
        </MenuItem>
      ))}
    </Menu>
  );
};

export default ColumnMenu;
